/**
 * Normalise customer shipping address JSON (profile ↔ checkout).
 */
import {
  CHECKOUT_COUNTRIES,
  countryToCodeFromGoogleOrLabel,
} from '../constants/checkoutCountries';
import { API_ENDPOINTS } from '../config/api';

/** @returns {{ street: string, apartment: string, city: string, state: string, postalCode: string, country: string } | null} */
export function parseCustomerShippingAddress(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const street = String(raw.street || raw.line1 || raw.address1 || '').trim();
  const apartment = String(raw.apartment || raw.line2 || raw.address2 || '').trim();
  const city = String(raw.city || '').trim();
  const state = String(raw.state || '').trim();
  const postalCode = String(raw.postalCode || raw.postcode || raw.zip || '').trim();
  const country = countryToCodeFromGoogleOrLabel(raw.country || 'AU');
  if (!street && !city) return null;
  return { street, apartment, city, state, postalCode, country };
}

export function isCompleteShippingAddress(addr) {
  if (!addr) return false;
  return !!(addr.street && addr.city && addr.state && addr.postalCode && addr.country);
}

export function formatShippingAddressSummary(addr) {
  if (!addr) return '';
  const parts = [
    addr.street,
    addr.apartment,
    addr.city,
    addr.state,
    addr.postalCode,
  ].filter(Boolean);
  const countryEntry = CHECKOUT_COUNTRIES.find((c) => c.code === addr.country);
  if (countryEntry) parts.push(countryEntry.label);
  else if (addr.country) parts.push(addr.country);
  return parts.join(', ');
}

/** Shape stored on Customer.shippingAddress (matches profile page). */
export function buildProfileShippingPayload(shippingAddress) {
  const countryLabel =
    CHECKOUT_COUNTRIES.find((c) => c.code === shippingAddress.country)?.label ||
    shippingAddress.country ||
    'Australia';
  return {
    street: shippingAddress.street,
    line1: shippingAddress.street,
    ...(shippingAddress.apartment
      ? { line2: shippingAddress.apartment, apartment: shippingAddress.apartment }
      : {}),
    city: shippingAddress.city,
    state: shippingAddress.state,
    postcode: shippingAddress.postalCode,
    country: countryLabel,
  };
}

/**
 * Save the address used at checkout to the customer profile (logged-in shop customers).
 * Non-blocking for checkout — callers should catch errors.
 */
export async function persistCustomerShippingAfterOrder({
  getAuthHeaders,
  refreshCustomer,
  shippingAddress,
  phone,
}) {
  if (!shippingAddress || !isCompleteShippingAddress(shippingAddress)) return;

  const res = await fetch(API_ENDPOINTS.customerProfile.me, {
    method: 'PUT',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...(phone ? { phone } : {}),
      shippingAddress: buildProfileShippingPayload(shippingAddress),
    }),
  });

  if (!res.ok) {
    throw new Error(`Profile address save failed (${res.status})`);
  }

  if (typeof refreshCustomer === 'function') {
    await refreshCustomer();
  }
}
